import { CompanyHistoryProps, CompanyHistoryEditorProps, ColorScheme, HistoryItem } from './CompanyHistory.types';
import { v4 as uuidv4 } from 'uuid';

/**
 * 기본 연혁 항목
 */
const defaultHistoryItems: HistoryItem[] = [
  {
    id: uuidv4(),
    year: '2010',
    title: '회사 설립',
    description: '혁신적인 비전을 가지고 회사가 설립되었습니다.',
    image: ''
  },
  {
    id: uuidv4(),
    year: '2012',
    title: '첫 제품 출시',
    description: '시장에서 좋은 반응을 얻은 첫 제품을 출시했습니다.',
    image: ''
  },
  {
    id: uuidv4(),
    year: '2015',
    title: '해외 시장 진출',
    description: '글로벌 시장으로 확장하여 아시아 지역에 첫 해외 지사를 설립했습니다.',
    image: ''
  },
  {
    id: uuidv4(),
    year: '2018',
    title: '주요 투자 유치',
    description: '대규모 투자 유치에 성공하여 사업 확장의 기반을 마련했습니다.',
    image: ''
  },
  {
    id: uuidv4(),
    year: '2020',
    title: '기술 혁신상 수상',
    description: '혁신적인 기술력을 인정받아 국내 기술 혁신상을 수상했습니다.',
    image: ''
  },
  {
    id: uuidv4(),
    year: '2023',
    title: '신규 서비스 론칭',
    description: '고객 중심의 혁신적인 서비스를 성공적으로 출시했습니다.',
    image: ''
  }
];

/**
 * 기본 컬러 스키마
 */
const defaultColorScheme: ColorScheme = {
  primary: '#3498db',
  secondary: '#f1c40f',
  background: '#ffffff',
  text: '#333333'
};

/**
 * 원본 props를 회사 연혁 컴포넌트에 맞게 변환
 * 
 * @param originalProps 원본 props
 * @returns 변환된 회사 연혁 컴포넌트 props
 */
export function adaptCompanyHistoryProps(originalProps: any): CompanyHistoryProps {
  // 기본값 설정
  const originalContent = originalProps?.content || {};
  const originalData = originalProps?.data || {};
  const originalComponentProps = originalProps?.componentProps || {};
  
  // 제목 값 추출 (여러 경로에서 가져오기)
  const title = originalProps?.title || 
                originalContent?.title || 
                originalComponentProps?.title ||
                "회사 연혁";
  
  // 부제목 값 추출 (여러 경로에서 가져오기)
  const subtitle = originalProps?.subtitle || 
                  originalContent?.subtitle || 
                  originalComponentProps?.subtitle ||
                  "저희 회사의 성장 과정을 소개합니다";
  
  // 타임라인 스타일 추출
  const timelineStyle = originalProps?.timelineStyle || 
                      originalContent?.timelineStyle || 
                      originalComponentProps?.timelineStyle || 
                      'vertical';
  
  // 애니메이션 여부 추출
  // 명시적 false 값 처리를 위해 undefined 체크
  const animation = originalProps?.animation !== undefined ? originalProps.animation : 
                   originalContent?.animation !== undefined ? originalContent.animation : 
                   originalComponentProps?.animation !== undefined ? originalComponentProps.animation : 
                   true;
  
  // 색상 스키마 추출
  const colorScheme = {
    ...defaultColorScheme,
    ...(originalProps?.colorScheme || {}),
    ...(originalContent?.colorScheme || {}),
    ...(originalComponentProps?.colorScheme || {})
  };
  
  // 이미지 크기 추출
  const imageSize = originalProps?.imageSize || 
                   originalContent?.imageSize || 
                   originalComponentProps?.imageSize;
  
  const imageWidth = originalProps?.imageWidth || 
                    originalContent?.imageWidth || 
                    originalComponentProps?.imageWidth || 
                    imageSize || 
                    150;
  
  const imageHeight = originalProps?.imageHeight || 
                     originalContent?.imageHeight || 
                     originalComponentProps?.imageHeight || 
                     imageSize || 
                     150;
  
  // 연혁 항목 추출
  let historyItems = defaultHistoryItems;
  if (originalProps?.historyItems && Array.isArray(originalProps.historyItems)) {
    historyItems = originalProps.historyItems;
  } else if (originalContent?.historyItems && Array.isArray(originalContent.historyItems)) {
    historyItems = originalContent.historyItems;
  } else if (originalComponentProps?.historyItems && Array.isArray(originalComponentProps.historyItems)) {
    historyItems = originalComponentProps.historyItems;
  }
  
  // 스타일 병합
  const style = {
    ...(originalProps?.style || {}),
    ...(originalComponentProps?.style || {})
  };
  
  // className 설정
  const className = originalProps?.className || originalComponentProps?.className || '';

  // 변환된 props 반환
  const adaptedProps: CompanyHistoryProps = {
    id: originalProps?.id || '',
    title,
    subtitle,
    timelineStyle,
    colorScheme,
    animation,
    imageWidth,
    imageHeight,
    imageSize,
    historyItems,
    style,
    className,
    content: { 
      ...originalContent,
      title,
      subtitle,
      timelineStyle,
      colorScheme,
      animation,
      imageWidth,
      imageHeight,
      imageSize,
      historyItems
    },
    componentProps: {
      ...originalComponentProps,
      title,
      subtitle,
      timelineStyle,
      colorScheme,
      animation,
      imageWidth,
      imageHeight,
      imageSize,
      historyItems
    },
    data: originalProps?.data || {}
  };

  // 원본 props의 모든 속성 중 이미 처리한 속성 제외하고 나머지 복사
  const excludeKeys = ['id', 'title', 'subtitle', 'timelineStyle', 'colorScheme', 'animation', 
                      'imageWidth', 'imageHeight', 'imageSize', 'historyItems', 'style', 
                      'className', 'content', 'componentProps', 'data'];
  
  Object.keys(originalProps).forEach(key => {
    if (!excludeKeys.includes(key)) {
      adaptedProps[key] = originalProps[key];
    }
  });

  return adaptedProps;
}

/**
 * 원본 props를 회사 연혁 에디터 컴포넌트에 맞게 변환
 * 
 * @param originalProps 원본 props
 * @returns 변환된 회사 연혁 에디터 컴포넌트 props
 */
export function adaptCompanyHistoryEditorProps(originalProps: any): CompanyHistoryEditorProps {
  // 기본 props 변환
  const adaptedProps = adaptCompanyHistoryProps(originalProps);
  
  // 에디터 관련 추가 props
  return {
    ...adaptedProps,
    isSelected: originalProps.isSelected || false,
    isPreview: originalProps.isPreview || false,
    viewMode: originalProps.viewMode || 'pc',
    updateComponent: originalProps.updateComponent || (() => {})
  };
} 